import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Edit,
  Visibility,
  Star,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import uploadService from '../../services/uploadService';
import DialogHeader from '../Common/DialogHeader';
import ImageLightbox from '../ImageGallery/ImageLightbox';

// Rendezhető képkártya. A sorrend dnd-kit-tel húzható: egérrel rövid elmozdulás után, érintésen
// hosszú nyomással (a korábbi HTML5 draggable telefonon/tableten nem működött).
const SortableImageCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <Card
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 2 : 'auto',
        position: 'relative',
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
        // Hosszú nyomásnál ne jelenjen meg a böngésző kép-menüje / szövegkijelölés
        WebkitTouchCallout: 'none',
        userSelect: 'none',
      }}
    >
      {children}
    </Card>
  );
};

const ACTION_BUTTON_SX = { width: 40, height: 40 };

const ImageUploader = ({
  images = [],
  onImagesChange,
  maxImages = 10,
  allowFeatured = true,
  allowDelete = true,
  uploadType,
  entityId
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, image: null });
  const [previewDialog, setPreviewDialog] = useState({ open: false, index: 0 });
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Drag and drop kezelés
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [images, maxImages, onImagesChange]); // Függőségek frissítve

  const handleFiles = async (files) => {
    if (!uploadType || !entityId) { // ÚJ: Ellenőrizzük, hogy megvannak-e a szükséges adatok
      setUploadError('Hiányzó feltöltési típus vagy entitás ID.');
      return;
    }

    if (images.length >= maxImages) {
      setUploadError(`Maximum ${maxImages} képet tölthet fel!`);
      return;
    }

    setUploading(true);
    setUploadError(null);
    const uploadedImages = [];
    const filesToUpload = Array.from(files).filter(file => file.type.startsWith('image/'));

    for (let i = 0; i < filesToUpload.length && images.length + uploadedImages.length < maxImages; i++) {
      const file = filesToUpload[i];
      try {
        // MÓDOSÍTVA: Átadjuk a uploadType és entityId paramétereket
        const response = await uploadService.uploadImage(file, uploadType, entityId);
        if (response.success && response.url) {
          const newImage = {
            id: `temp-${Date.now()}-${i}`,
            url: response.url,
            title: file.name.split('.')[0] || 'Névtelen kép',
            description: '',
            isFeatured: false,
            sortOrder: images.length + uploadedImages.length,
          };
          uploadedImages.push(newImage);
        } else {
          setUploadError(response.message || `Hiba a(z) ${file.name} feltöltése során.`);
          break;
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        setUploadError(error.message || `Szerver hiba a(z) ${file.name} feltöltése során.`);
        break;
      }
    }

    if (uploadedImages.length > 0) {
      onImagesChange([...images, ...uploadedImages]);
    }
    setUploading(false);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = null; // Fájl input mező törlése a kiválasztás után
    }
  };

  const handleDelete = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    // MÓDOSÍTVA: Frissítjük a sortOrder-t a törlés után
    const reorderedImages = updatedImages.map((img, index) => ({ ...img, sortOrder: index }));
    onImagesChange(reorderedImages);
  };


  const handleEdit = (image) => {
    setEditDialog({ open: true, image: { ...image } });
  };

  const handleSaveEdit = () => {
    const updatedImages = images.map(img => 
      img.id === editDialog.image.id ? editDialog.image : img
    );
    onImagesChange(updatedImages);
    setEditDialog({ open: false, image: null });
  };

  const handleSetFeatured = (imageId) => { // MÓDOSÍTVA: isFeatured
    const updatedImages = images.map(img => ({
      ...img,
      isFeatured: img.id === imageId // MÓDOSÍTVA: isFeatured
    }));
    onImagesChange(updatedImages);
  };

  const handlePreview = (index) => {
    setPreviewDialog({ open: true, index });
  };


  const handleSortEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = images.findIndex(img => img.id === active.id);
    const newIndex = images.findIndex(img => img.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onImagesChange(arrayMove(images, oldIndex, newIndex).map((img, index) => ({ ...img, sortOrder: index })));
  };

  return (
    <Box>
      {/* Feltöltött képek – felül */}
      {images.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Feltöltött képek ({images.length}/{maxImages})
          </Typography>
          {images.length > 1 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              A sorrend módosításához húzza a képet (telefonon: nyomja hosszan, majd húzza).
            </Typography>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSortEnd}>
          <SortableContext items={images.map(img => img.id)} strategy={rectSortingStrategy}>
          <Grid container spacing={1.5}>
            {images.map((image, index) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={image.id}>
                <SortableImageCard id={image.id}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={image.url} // Itt már a helyes 'url' property-t használjuk
                      alt={image.title}
                      draggable={false}
                      onClick={() => handlePreview(index)}
                      sx={{
                        width: '100%',
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                        display: 'block',
                        cursor: 'zoom-in'
                      }}
                    />
                    {image.isFeatured && (
                      <Chip
                        icon={<Star />}
                        label="Kiemelt"
                        color="warning"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8
                        }}
                      />
                    )}
                  </Box>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="subtitle2" noWrap>
                      {image.title}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', mt: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(index)}
                        title="Előnézet"
                        aria-label="Előnézet"
                        sx={ACTION_BUTTON_SX}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(image)}
                        title="Szerkesztés"
                        aria-label="Szerkesztés"
                        sx={ACTION_BUTTON_SX}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      {allowFeatured && (
                        <IconButton
                          size="small"
                          onClick={() => handleSetFeatured(image.id)}
                          color={image.isFeatured ? 'warning' : 'default'}
                          title="Kiemelt kép"
                          aria-label="Kiemelt kép"
                          sx={ACTION_BUTTON_SX}
                        >
                          <Star fontSize="small" />
                        </IconButton>
                      )}
                      {allowDelete && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(image.id)}
                          color="error"
                          title="Törlés"
                          aria-label="Törlés"
                          sx={ACTION_BUTTON_SX}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </SortableImageCard>
              </Grid>
            ))}
          </Grid>
          </SortableContext>
          </DndContext>
        </Box>
      )}

      {/* Feltöltési terület – alul */}
      <Card
        sx={{
          mb: 3,
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          bgcolor: dragActive ? 'primary.50' : 'background.paper',
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CloudUpload sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Húzza ide a képeket vagy kattintson a tallózáshoz
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Támogatott formátumok: JPG, PNG, WebP, GIF (max. {maxImages} kép)
          </Typography>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInput}
            style={{ display: 'none' }}
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <Button variant="contained" component="span" disabled={uploading || images.length >= maxImages}>
              Képek kiválasztása
            </Button>
          </label>
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary">
                Feltöltés folyamatban...
              </Typography>
            </Box>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Szerkesztés dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, image: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<Edit />} title="Kép szerkesztése" />
        </DialogTitle>
        <DialogContent>
          {editDialog.image && (
            <Box>
              <img
                src={editDialog.image.url}
                alt={editDialog.image.title}
                style={{
                  width: '100%',
                  height: 200,
                  objectFit: 'cover',
                  marginBottom: 16,
                  borderRadius: 4
                }}
              />
              <TextField
                fullWidth
                label="Cím"
                value={editDialog.image.title}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  image: { ...editDialog.image, title: e.target.value }
                })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Leírás"
                multiline
                rows={3}
                value={editDialog.image.description}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  image: { ...editDialog.image, description: e.target.value }
                })}
                inputProps={{ maxLength: 150 }}
                helperText={`${(editDialog.image.description || '').length}/150 – ez a szöveg kicsiben, a kép alatt jelenik meg (pl. hivatalos forrás megjelölésére használható)`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setEditDialog({ open: false, image: null })}>
            Mégse
          </Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Mentés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Előnézet – lapozható nagyítás */}
      <ImageLightbox
        open={previewDialog.open}
        images={images}
        startIndex={previewDialog.index}
        onClose={() => setPreviewDialog({ open: false, index: 0 })}
      />
    </Box>
  );
};

export default ImageUploader;